<template>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.videoList.detailModal.new.buttonText')"
      @click="videoModalOpen = true"
    />
    <export
      url="/videos/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.videos')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <video-modal v-model:open="videoModalOpen" v-model:video="editedVideo" @saved="refetch" />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  >
    <template #file="{ item }">
      <file v-slot="{ url, type }" :src="`/videos/${item.id}/content`" class="py-4 w-full">
        <box :aspect="0.6" class="medium m-auto">
          <medium v-if="url" class="w-auto h-full" :url="url" :type="type" />
        </box>
      </file>
    </template>
  </data-table>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="ts" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';
import Medium from '@utils/Medium.vue';
import File from '@utils/File.vue';

import type { Filter } from '@utils/filter/Filter.vue';
import Box from '@utils/Box.vue';
import { useDelete } from '@hooks/api';
import VideoModal from './VideoModal.vue';
import type { HydraCollection, Video, VideoCategory } from '@/entities/Entities';
import { translationFor } from '@/utils/api';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const totalFilter = computed(() => ({ ...filter.value }));

const videoModalOpen = ref(false);
const editedVideo = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();
const translate = translationFor();

const [deleteVideo, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.videoList.actions.edit'),
    action: (item: Video) => {
      videoModalOpen.value = true;
      editedVideo.value = item;
    },
  },
  {
    title: t('pages.videoList.actions.delete'),
    action: async (item: Video) => {
      if (await deleteVideo(`/videos/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.videoList.fieldGroups.video'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'file',
    title: t('video.file'),
    group: t('pages.videoList.fieldGroups.video'),
    visible: true,
    sortable: false,
  },
  {
    key: 'orderNumber',
    title: t('video.orderNumber'),
    group: t('pages.videoList.filterGroups.category'),
    visible: true,
  },
  {
    key: 'title',
    title: t('video.title'),
    group: t('pages.videoList.fieldGroups.video'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.videoList.filterGroups.video'),
      options: {
        label: t('video.title'),
      },
    },
  },
  {
    key: 'description',
    title: t('video.description'),
    group: t('pages.videoList.fieldGroups.video'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.videoList.filterGroups.video'),
      options: {
        label: t('video.description'),
      },
    },
  },
  {
    key: 'categories',
    title: t('video.categories'),
    group: t('pages.videoList.fieldGroups.video'),
    formatter: (categories: VideoCategory[]) =>
      categories.map((category) => translate(category, 'title')).join(', '),
    visible: true,
    sortable: false,
    filter: {
      type: 'tags',
      group: t('pages.videoList.filterGroups.video'),
      options: {
        options: collOptionsProvider<VideoCategory>('/video_categories', (category) => ({
          label: translate(category, 'title') ?? '',
          value: category.id,
        })),
        placeholder: t('select', { what: t('video.videos') }),
        search: false,
        label: t('video.categories'),
      },
    },
  },
  {
    key: 'language',
    csvTranslations: {
      de: t('language.de'),
      en: t('language.en'),
      fr: t('language.fr'),
      it: t('language.it'),
    },
    title: t('video.language'),
    group: t('pages.videoList.fieldGroups.video'),
    visible: true,
    formatter: (item: string) => t(`language.${item}`),
    filter: {
      type: 'dropdown',
      group: t('pages.videoList.filterGroups.video'),
      options: {
        options: enumOptionsProvider('/enums/language', (v) => ({
          value: v,
          label: t(`language.${v}`),
        })),
        label: t('video.language'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<Video>>('/videos', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  })
);

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

